import PropTypes from 'prop-types';
import { Controller, useFieldArray } from 'react-hook-form';
import { cn } from '#/lib/utils';

import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import { Button } from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import { createEmptyContactRow } from '#/modules/srmMgmt/supplier/helper/buildSupplierWritePayload';

const SupplierContactsEditor = ({ control, register, errors, setValue }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'contacts',
  });

  function handleAddContact() {
    append(createEmptyContactRow());
  }

  function handleDefaultChange(index, checked) {
    setValue(`contacts.${index}.isDefault`, Boolean(checked), {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (!checked) {
      return;
    }

    fields.forEach((_, otherIndex) => {
      if (otherIndex === index) {
        return;
      }
      setValue(`contacts.${otherIndex}.isDefault`, false, {
        shouldDirty: true,
      });
    });
  }

  return (
    <div className="form-section @container/form-section">
      <div className="form-section__header">
        <h2 className="form-block__title mb-0!">聯絡人</h2>
        <Button type="button" size="sm" onClick={handleAddContact}>
          新增聯絡人
        </Button>
      </div>

      {fields.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          尚未新增聯絡人；儲存時將以空白資料儲存。
        </p>
      ) : null}

      {errors?.contacts?.message ? (
        <p className="mt-3 text-sm text-red-500">{errors.contacts.message}</p>
      ) : null}

      {fields.length > 0 ? (
        <div
          className={cn(
            'form-section__body',
            'grid grid-cols-1 gap-3',
            '@lg/form-section:grid-cols-2',
          )}
        >
          {fields.map((field, idx) => (
            <div
              key={field.id}
              className={cn(
                'px-3 py-5',
                'bg-gray-50',
                'border border-gray-200 rounded-md',
              )}
            >
              <FloatingInput
                id={`contacts.${idx}.name`}
                label={`姓名 #${idx + 1}`}
                register={register}
                errors={errors}
              />
              <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2">
                <FloatingInput
                  id={`contacts.${idx}.department`}
                  label="部門"
                  register={register}
                  errors={errors}
                />
                <FloatingInput
                  id={`contacts.${idx}.title`}
                  label="職稱"
                  register={register}
                  errors={errors}
                />
              </div>
              <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2">
                <FloatingInput
                  id={`contacts.${idx}.phone`}
                  label="電話"
                  register={register}
                  errors={errors}
                />
                <FloatingInput
                  id={`contacts.${idx}.mobile`}
                  label="手機"
                  register={register}
                  errors={errors}
                />
              </div>
              <FloatingInput
                id={`contacts.${idx}.email`}
                label="Email"
                register={register}
                errors={errors}
              />
              <FloatingInput
                id={`contacts.${idx}.remark`}
                label="備註"
                register={register}
                errors={errors}
              />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Controller
                  name={`contacts.${idx}.isDefault`}
                  control={control}
                  render={({ field: checkboxField }) => (
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        預設聯絡人
                      <Checkbox
                        checked={Boolean(checkboxField.value)}
                        onCheckedChange={(checked) => handleDefaultChange(idx, checked)}
                      />
                    </label>
                  )}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => remove(idx)}
                >
                    移除項目
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

SupplierContactsEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
};

export default SupplierContactsEditor;
